import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Cite from '../components/Cite';
import { useLanguage } from '../../../hooks/useLanguage';

const ResearchVectorOntologies: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? "Working on something where the substrate matters? Let's compare notes."
          : 'Werk je aan iets waarbij de AI-laag ertoe doet? Laten we kennis vergelijken.'}
      </h2>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  return (
    <WebsiteLayout customCta={customCta}>
      <Helmet>
        <title>
          {en
            ? 'A shared ontological framework for humans and machines. · Thoughtful Oasis'
            : 'Een gedeeld ontologisch kader voor mens en machine. · Thoughtful Oasis'}
        </title>
        <meta
          name="description"
          content="Machines reason in vector spaces. Humans reason in language. Vector ontologies — vector spaces with named, interpretable axes — are the shared framework where both can meet."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/research/vector-ontologies" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Thoughtful Oasis" />
        <meta
          property="og:title"
          content="A shared ontological framework for humans and machines — Thoughtful Oasis"
        />
        <meta
          property="og:description"
          content="Machines reason in vector spaces. Humans reason in language. Vector ontologies — vector spaces with named, interpretable axes — are the shared framework where both can meet."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/research/vector-ontologies" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="A shared ontological framework for humans and machines — Thoughtful Oasis"
        />
        <meta
          name="twitter:description"
          content="Machines reason in vector spaces. Humans reason in language. Vector ontologies — vector spaces with named, interpretable axes — are the shared framework where both can meet."
        />
        <meta name="twitter:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "ScholarlyArticle",
          "headline": "To Be or Not To Be: Vector ontologies as a truly formal ontological framework",
          "author": [{"@type": "Person", "name": "Kaspar Rothenfusser"}],
          "datePublished": "2025",
          "publisher": {"@type": "Organization", "name": "Thoughtful Oasis", "url": "https://thoughtful-oasis.com/"},
          "url": "https://thoughtful-oasis.com/research/vector-ontologies",
          "sameAs": "https://arxiv.org/abs/2505.14940"
        }`}</script>
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Research", "item": "https://thoughtful-oasis.com/research"},
            {"@type": "ListItem", "position": 2, "name": "Vector ontologies", "item": "https://thoughtful-oasis.com/research/vector-ontologies"}
          ]
        }`}</script>
      </Helmet>

      <section className="post">
        <div className="post-layout">
          <aside className="post-aside">
            <p className="post-aside__byline">
              Kaspar Rothenfusser, 2025 ·{' '}
              <a
                className="link-quiet"
                href="https://arxiv.org/abs/2505.14940"
                target="_blank"
                rel="noopener noreferrer"
              >
                arXiv 2505.14940
              </a>{' '}
              ·{' '}
              <Link className="link-quiet" to="/research">
                {en ? 'All research' : 'Al het onderzoek'}
              </Link>
            </p>
            <Cite
              filename="rothenfusser-2025-vector-ontologies"
              apa="Rothenfusser, K. (2025). To be or not to be: Vector ontologies as a truly formal ontological framework (arXiv:2505.14940). arXiv. https://arxiv.org/abs/2505.14940"
              bibtex={'@article{rothenfusser2025vectorontologies,\n  author        = {Rothenfusser, Kaspar},\n  title         = {To Be or Not to Be: Vector Ontologies as a Truly Formal Ontological Framework},\n  year          = {2025},\n  eprint        = {2505.14940},\n  archivePrefix = {arXiv},\n  primaryClass  = {cs.AI},\n  url           = {https://arxiv.org/abs/2505.14940}\n}'}
            />
          </aside>
          <article className="post__main">
            <p className="post__meta">
              {en
                ? 'Plain-language summary · Theory · 2025'
                : 'Samenvatting in gewone taal · Theorie · 2025'}
            </p>
            <h1>
              {en
                ? 'A shared ontological framework for humans and machines.'
                : 'Een gedeeld ontologisch kader voor mens en machine.'}
            </h1>
            <p className="post__lede">
              {en
                ? 'Machines already reason in vector spaces. Humans represent concepts in something very similar. This paper presents vector ontologies — vector spaces with named, interpretable axes — as the shared framework between the two.'
                : 'Machines redeneren al in vectorruimtes. Mensen representeren concepten in iets dat er sterk op lijkt. Deze paper presenteert vector-ontologieën — vectorruimtes met benoemde, interpreteerbare assen — als het gedeelde kader tussen beide.'}
            </p>

            {en ? (
              <div className="post__body" lang="en">
                <p>
                  The field of "formal ontology" has produced genuinely useful work — BFO, DOLCE, GFO —
                  careful taxonomies that underpin medical records, knowledge graphs, regulatory data
                  exchange. But this paper shows that none of these are formal in the sense that Husserl, who
                  coined the term, intended. And that the distinction matters.
                </p>

                <h2>What the paper claims</h2>
                <p>
                  Husserl defined formal structures as <em>a priori</em> valid and completely empty of
                  content. Classical ontologies do not satisfy either criterion. Distinguishing{' '}
                  <em>endurants</em> from <em>perdurants</em>, objects from events, qualities from
                  relations — these are categories abstracted from how humans perceive reality. They are
                  useful, but they are foundational ontologies, not formal ones.
                </p>
                <p>
                  The paper presents vector spaces as the genuinely formal alternative. A vector space is
                  defined by axioms that say nothing about the world. You can fill it with any content. The
                  structure does not change.
                </p>

                <h2>Vector ontologies</h2>
                <p>
                  A <strong>vector ontology</strong> is a finite-dimensional vector space whose basis vectors
                  are named, interpretable domain features — <em>urgency, jurisdiction, structural risk</em>,
                  whatever the domain requires. Entities become points. Categories become regions.
                  Relationships become distances and projections.
                </p>
                <p>
                  The paper points out that neural networks already perform ontological tasks — image
                  recognition, classification, language understanding — and that all processing inside them
                  happens through high-dimensional vector operations. The tasks are ontological in nature;
                  the structure they are performed in is a vector space. This is taken as evidence that
                  ontology in vector spaces is not only possible but already happening.
                </p>

                <h2>Ontology becomes geometry</h2>
                <p>
                  The paper demonstrates that once the substrate is geometric, classical ontological concepts
                  re-emerge without being stipulated. <strong>Parthood</strong> becomes containment — the
                  engine is part of the car when its coordinates fall inside the car's region.{' '}
                  <strong>Causation</strong> becomes linear dependence. <strong>Similarity</strong> becomes
                  reconstruction distance — <em>apple</em> and <em>pear</em> differ in a few coordinates;{' '}
                  <em>apple</em> and <em>operating system</em> differ in many. <strong>Time</strong> becomes
                  an axis along which existence functions are evaluated.
                </p>
                <p>The classical taxonomies turn out to be shadows of geometric facts that were always there.</p>

                <h2>Humans, too</h2>
                <p>
                  The paper also discusses a line of evidence in cognitive science, going back to Peter
                  Gärdenfors, suggesting that human concepts live in something that behaves like a vector
                  space — a "conceptual space" with named dimensions. We learn <em>red</em> as a coordinate
                  on the colour axis. We learn parthood through containment in space and time.
                </p>

                <h2>A shared framework</h2>
                <p>
                  Machines already perform ontological reasoning in vector spaces. Humans represent concepts
                  in something that behaves like one. Vector ontologies — with named, interpretable axes —
                  are the shared ontological framework between the two. A framework where a machine's
                  representation is not just effective but readable, and where a human's understanding can
                  be expressed in a form a machine can work with.
                </p>
                <blockquote>
                  Vector ontology is the formal-as-Husserl-meant-it ontology that artificial intelligence has
                  been pointing at without knowing it.
                </blockquote>

                <h2>What follows</h2>
                <p>
                  The framework in this paper is theoretical. The obvious follow-up is the empirical one:{' '}
                  <em>can we actually build it?</em> For that we took musical genres, constructed an eight-axis
                  vector ontology, and asked a standard LLM to populate it with no fine-tuning. That experiment
                  is in the{' '}
                  <Link className="link-inline" to="/research/worldview">
                    companion paper
                  </Link>
                  .
                </p>
                <p>
                  The full mathematics — convex subspaces, existence functions, reconstruction-distance
                  proofs — are in the{' '}
                  <a
                    className="link-inline"
                    href="https://arxiv.org/abs/2505.14940"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    original paper on arXiv
                  </a>
                  .
                </p>
              </div>
            ) : (
              <div className="post__body" lang="nl">
                <p>
                  Het vakgebied "formele ontologie" heeft nuttig werk opgeleverd — BFO, DOLCE, GFO —
                  zorgvuldige taxonomieën die onder medische dossiers, knowledge graphs en regelgeving liggen.
                  Maar deze paper laat zien dat geen ervan formeel is in de zin die Husserl, die de term
                  muntte, bedoelde. En dat het onderscheid ertoe doet.
                </p>

                <h2>Wat de paper stelt</h2>
                <p>
                  Husserl definieerde formele structuren als <em>a priori</em> geldig en volledig leeg van
                  inhoud. Klassieke ontologieën voldoen aan geen van beide. Het onderscheid tussen{' '}
                  <em>endurants</em> en <em>perdurants</em>, objecten en gebeurtenissen, kwaliteiten en
                  relaties — dat zijn categorieën geabstraheerd uit hoe mensen de werkelijkheid waarnemen.
                  Nuttig, maar het zijn fundamentele ontologieën, geen formele.
                </p>
                <p>
                  De paper presenteert vectorruimtes als het werkelijk formele alternatief. Een vectorruimte
                  wordt gedefinieerd door axioma's die niets over de wereld zeggen. Je kunt er elke inhoud in
                  stoppen. De structuur verandert niet.
                </p>

                <h2>Vector-ontologieën</h2>
                <p>
                  Een <strong>vector-ontologie</strong> is een eindig-dimensionale vectorruimte waarvan de
                  basisvectoren benoemde, interpreteerbare domeinkenmerken zijn — <em>urgentie, jurisdictie,
                  structureel risico</em>, wat het domein ook vereist. Entiteiten worden punten. Categorieën
                  worden gebieden. Relaties worden afstanden en projecties.
                </p>
                <p>
                  De paper wijst erop dat neurale netwerken al ontologische taken uitvoeren —
                  beeldherkenning, classificatie, taalbegrip — en dat alle verwerking daarbinnen gebeurt via
                  hoog-dimensionale vectorbewerkingen. De taken zijn ontologisch van aard; de structuur waarin
                  ze plaatsvinden is een vectorruimte. Dit wordt als bewijs gezien dat ontologie in
                  vectorruimtes niet alleen mogelijk is, maar al gebeurt.
                </p>

                <h2>Ontologie wordt meetkunde</h2>
                <p>
                  De paper laat zien dat zodra de AI-laag geometrisch is, klassieke ontologische concepten
                  vanzelf opduiken. <strong>Deel-zijn</strong> wordt bevatting — de motor is deel van de auto
                  als zijn coördinaten binnen het gebied van de auto vallen. <strong>Causaliteit</strong>{' '}
                  wordt lineaire afhankelijkheid. <strong>Gelijkenis</strong> wordt reconstructie-afstand —{' '}
                  <em>appel</em> en <em>peer</em> verschillen in een paar coördinaten; <em>appel</em> en{' '}
                  <em>besturingssysteem</em> in veel. <strong>Tijd</strong> wordt een as waarlangs
                  bestaansfuncties geëvalueerd worden.
                </p>
                <p>De klassieke taxonomieën blijken schaduwen van geometrische feiten die er altijd al waren.</p>

                <h2>Mensen ook</h2>
                <p>
                  De paper bespreekt ook een lijn van bewijs in de cognitiewetenschap, teruggaand tot Peter
                  Gärdenfors, die suggereert dat menselijke concepten leven in iets dat zich als een
                  vectorruimte gedraagt — een "conceptuele ruimte" met benoemde dimensies. We leren{' '}
                  <em>rood</em> als een coördinaat op de kleur-as. We leren deel-zijn door bevatting in ruimte
                  en tijd.
                </p>

                <h2>Een gedeeld kader</h2>
                <p>
                  Machines redeneren al ontologisch in vectorruimtes. Mensen representeren concepten in iets
                  dat zich als zodanig gedraagt. Vector-ontologieën — met benoemde, interpreteerbare assen —
                  zijn het gedeelde ontologische kader tussen mens en machine. Een kader waarin de
                  representatie van een machine niet alleen effectief maar leesbaar is, en waarin menselijk
                  begrip uitgedrukt kan worden in een vorm waar een machine mee kan werken.
                </p>
                <blockquote>
                  Vector-ontologie is de formele-zoals-Husserl-het-bedoelde ontologie waar kunstmatige
                  intelligentie de hele tijd onbewust naar wees.
                </blockquote>

                <h2>Wat hierop volgt</h2>
                <p>
                  Het kader in deze paper is theoretisch. De voor de hand liggende vervolgvraag is de
                  empirische: <em>kunnen we het ook echt bouwen?</em> Daarvoor pakten we muziekgenres, bouwden
                  een vector-ontologie met acht assen, en vroegen een standaard-LLM om die te vullen zonder
                  fine-tuning. Dat experiment staat in de{' '}
                  <Link className="link-inline" to="/research/worldview">
                    companion paper
                  </Link>
                  .
                </p>
                <p>
                  De volledige wiskunde — convexe deelruimtes, bestaansfuncties, bewijzen rond
                  reconstructie-afstand — staat in de{' '}
                  <a
                    className="link-inline"
                    href="https://arxiv.org/abs/2505.14940"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    originele paper op arXiv
                  </a>
                  .
                </p>
              </div>
            )}
          </article>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default ResearchVectorOntologies;
